import React from 'react';
import { 
  AlertCircle, 
  Clock, 
  Pill, 
  Eye, 
  HeartCrack,
  FileText,
  TrendingUp,
  Stethoscope,
  Shield
} from 'lucide-react';

const CareToolsComparison = () => {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-muted/30 via-background to-secondary/10 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              What Changes When You Have the Right Tools
            </h2>
          </div>

          {/* Side-by-Side Container */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* LEFT SIDE: THE INVISIBLE BURDEN */}
            <div className="relative">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 dark:bg-amber-900/30 rounded-md flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600/60 dark:text-amber-400/60" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-600/70 dark:text-amber-400/70 uppercase tracking-wider">Before the right tools</p>
                  <h3 className="text-2xl font-bold text-muted-foreground">The Invisible Burden</h3>
                </div>
              </div>

              {/* Cluttered "Invisible Burden" Bubbles - Desktop scattered, Mobile stacked */}
              <div className="flex flex-col min-h-[520px] md:min-h-[580px]">
                
                {/* Bubbles Container - takes available space */}
                <div className="relative flex-1">
                  {/* Bubble 1: Can't Explain */}
                  <div className="md:absolute md:top-0 md:left-0 md:right-8 md:-rotate-1 mb-4 md:mb-0">
                    <div className="bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 rounded-md p-5 shadow-raised">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-500/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-bold italic">
                            "I know something's wrong, but I can't explain it."
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            You notice patterns, but without a record, your concerns feel like hunches.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 2: When Did It Start */}
                  <div className="md:absolute md:top-[100px] md:left-4 md:right-0 md:rotate-1 mb-4 md:mb-0">
                    <div className="bg-amber-50/30 dark:bg-amber-950/15 border border-amber-200/40 dark:border-amber-800/25 rounded-md p-5 shadow-raised">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-500/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-bold italic">
                            "When did that symptom start?"
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            The doctor asks, and you freeze. The timeline exists only in fragments.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 3: Medication Confusion */}
                  <div className="md:absolute md:top-[200px] md:left-0 md:right-6 md:-rotate-2 mb-4 md:mb-0">
                    <div className="bg-amber-50/35 dark:bg-amber-950/18 border border-amber-200/45 dark:border-amber-800/28 rounded-md p-5 shadow-raised">
                      <div className="flex items-start gap-3">
                        <Pill className="w-5 h-5 text-amber-500/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-bold italic">
                            "Did she take her meds today?"
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            You ask yourself this three times a day. There's no single source of truth.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 4: Sole Observer */}
                  <div className="md:absolute md:top-[300px] md:left-6 md:right-0 md:rotate-1 mb-4 md:mb-0">
                    <div className="bg-amber-50/25 dark:bg-amber-950/12 border border-amber-200/35 dark:border-amber-800/20 rounded-md p-5 shadow-raised">
                      <div className="flex items-start gap-3">
                        <Eye className="w-5 h-5 text-amber-500/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-bold italic">
                            "I'm the only one who really knows what's going on."
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            The weight of being the sole observer, translator, and historian.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terminal Banner: Invisible Labor - pushed to bottom */}
                <div className="mt-auto pt-6">
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30 rounded-md p-5 animate-pulse-soft transform -rotate-1">
                    <div className="flex items-center gap-3 mb-2">
                      <HeartCrack className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      <h4 className="font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wide text-sm">
                        Invisible Labor
                      </h4>
                    </div>
                    <p className="text-amber-700/80 dark:text-amber-300/80 text-sm leading-relaxed">
                      You carry the full picture in your head. No one else sees it, and no one can help carry it. That's exhausting.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mood Tags */}
              <div className="mt-6 text-center">
                <p className="text-muted-foreground/60 font-bold tracking-wide">
                  Uncertain • Invisible • Alone
                </p>
              </div>
            </div>

            {/* RIGHT SIDE: THE CLEAR RECORD */}
            <div className="relative">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-md flex items-center justify-center shadow-raised">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-primary/80 uppercase tracking-wider">With Care Tools</p>
                  <h3 className="text-2xl font-bold text-foreground">The Clear Record</h3>
                </div>
              </div>

              {/* Organized "Clear Record" Stack */}
              <div className="relative space-y-4 pl-8">
                {/* Visual Spine */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-primary/40 via-teal-400/40 to-primary/20 rounded-full" />

                {/* Item 1: Timeline of Facts */}
                <div className="relative animate-float" style={{ animationDelay: '0s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-raised shadow-primary/30" />
                  <div className="rounded-md p-5 shadow-floating shadow-primary/5 hover:shadow-overlay hover:shadow-primary/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">A Timeline of Facts</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          Symptoms, changes, and observations documented as they happen.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 2: Medication Accountability */}
                <div className="relative animate-float" style={{ animationDelay: '0.5s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-teal-500 rounded-full shadow-raised shadow-teal-500/30" />
                  <div className="bg-teal-50/50 dark:bg-teal-950/20 border border-teal-200/40 dark:border-teal-800/30 rounded-md p-5 shadow-floating shadow-teal-500/5 hover:shadow-overlay hover:shadow-teal-500/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 dark:bg-teal-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Pill className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Medication Accountability</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          Clear logs that answer "Did she take it?" without guessing.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 3: Pattern Recognition */}
                <div className="relative animate-float" style={{ animationDelay: '1s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-raised shadow-primary/30" />
                  <div className="rounded-md p-5 shadow-floating shadow-primary/5 hover:shadow-overlay hover:shadow-primary/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Pattern Recognition</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          See trends over weeks, not scattered impressions over months.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 4: Credibility at Appointments */}
                <div className="relative animate-float" style={{ animationDelay: '1.5s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-teal-500 rounded-full shadow-raised shadow-teal-500/30" />
                  <div className="bg-teal-50/50 dark:bg-teal-950/20 border border-teal-200/40 dark:border-teal-800/30 rounded-md p-5 shadow-floating shadow-teal-500/5 hover:shadow-overlay hover:shadow-teal-500/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 dark:bg-teal-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Stethoscope className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Credibility at Appointments</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          Walk in with data. The doctor sees you differently.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terminal Banner: The Frontline Toolkit - BOLD PAYOFF */}
                <div className="relative">
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-primary to-teal-500 rounded-full shadow-floating shadow-primary/30" />
                  <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-teal-50 dark:from-primary/20 dark:via-primary/10 dark:to-teal-950/30 border-2 border-primary/30 dark:border-primary/40 rounded-md p-5 shadow-overlay shadow-primary/15">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-bold text-primary dark:text-primary uppercase tracking-wide text-sm">
                        The Frontline Toolkit
                      </h4>
                    </div>
                    <p className="text-foreground/80 dark:text-foreground/70 text-sm leading-relaxed">
                      Your observations are no longer invisible. They're documented, shareable, and impossible to dismiss. You're not just caring, you're managing with clarity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mood Tags */}
              <div className="mt-6 text-center">
                <p className="text-primary font-bold tracking-wide">
                  Documented • Credible • Prepared
                </p>
              </div>
            </div>
          </div>

          {/* Reinforcing Copy */}
          <div className="mt-20 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Your observations deserve to be seen.
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              When everything lives in your head, you carry it alone. When it is captured in a system,
              you have proof, patterns, and peace of mind, and the people around you can finally
              see the work you have been doing all along.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareToolsComparison;
